import { FC } from 'react';
import styled from '@emotion/styled';
import { Typography, MenuCard } from '@taleemabad/dsm/components';
import { mq, BreakpointSizes } from '@taleemabad/dsm/themes';

// Styled Components
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: var(--rk-spacing-xl);
  margin: 0 auto;

  ${mq(BreakpointSizes.xs)} {
    padding: var(--rk-spacing-lg);
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: var(--rk-spacing-xxxl);

  ${mq(BreakpointSizes.xs)} {
    margin-bottom: var(--rk-spacing-xxl);
  }
`;

const HeaderSpacing = styled.div`
  margin-top: var(--rk-spacing-sm);
`;

const AssessmentGrid = styled.div`
  display: grid;
  gap: var(--rk-spacing-lg);
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));

  ${mq(BreakpointSizes.xs)} {
    grid-template-columns: 1fr;
    gap: var(--rk-spacing-md);
  }
`;

const AssessmentCard = styled.div<{ disabled: boolean }>`
  border: 2px solid
    ${(props) =>
      props.disabled
        ? 'var(--md-sys-color-outline-variant)'
        : 'var(--md-sys-color-primary)'};
  border-radius: 16px;
  padding: var(--rk-spacing-xl);
  background-color: ${(props) =>
    props.disabled
      ? 'var(--md-sys-color-surface-variant)'
      : 'var(--md-sys-color-surface)'};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  transition: all 0.2s ease-in-out;
  position: relative;
  gap: var(--rk-spacing-md);
  align-items: center;
  box-shadow: ${(props) =>
    props.disabled ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.1)'};

  &:hover {
    transform: ${(props) => (props.disabled ? 'none' : 'translateY(-4px)')};
    box-shadow: ${(props) =>
      props.disabled ? 'none' : '0 8px 24px rgba(0, 0, 0, 0.15)'};
  }

  ${mq(BreakpointSizes.xs)} {
    padding: var(--rk-spacing-lg);
  }
`;

const CardSpacing = styled.div`
  margin-top: var(--rk-spacing-md);
`;

const ComingSoonBadge = styled.div`
  position: absolute;
  top: var(--rk-spacing-md);
  right: var(--rk-spacing-md);
  background-color: var(--md-sys-color-secondary);
  color: var(--md-sys-color-on-secondary);
  padding: 4px var(--rk-spacing-md);
  border-radius: var(--rk-spacing-md);
  font-size: 12px;
  font-weight: 500;
`;

const DescriptionText = styled.div`
  text-align: center;
`;

// Interfaces
export interface AssessmentType {
  id: number;
  key: string;
  title: string;
  description: string;
  icon: string;
  disabled: boolean;
}

interface FlnAssessmentTypeTemplateProps {
  assessmentTypes: AssessmentType[];
  onAssessmentTypeSelection: (id: number) => void;
}

export const FlnAssessmentTypeTemplate: FC<FlnAssessmentTypeTemplateProps> = ({
  assessmentTypes,
  onAssessmentTypeSelection,
}) => {
  return (
    <ContentContainer>
      <HeaderSection>
        <Typography variant="textTitleLarge">Choose Assessment Type</Typography>
        <HeaderSpacing />
        <Typography
          variant="textBodyMedium"
          color="--md-sys-color-on-surface-variant"
        >
          Select the type of FLN assessment you want to access
        </Typography>
      </HeaderSection>

      <AssessmentGrid>
        {assessmentTypes.map((assessmentType) => (
          <AssessmentCard
            key={assessmentType.id}
            disabled={assessmentType.disabled}
          >
            <MenuCard
              id={assessmentType.id}
              label={assessmentType.title}
              icon={assessmentType.icon}
              analyticsKey={`fln_assessment_${assessmentType.key}`}
              onClick={() => onAssessmentTypeSelection(assessmentType.id)}
            />
            <CardSpacing />
            <DescriptionText>
              <Typography
                variant="textBodyMedium"
                color="--md-sys-color-on-surface-variant"
              >
                {assessmentType.description}
              </Typography>
            </DescriptionText>

            {assessmentType.disabled && (
              <ComingSoonBadge>Coming Soon</ComingSoonBadge>
            )}
          </AssessmentCard>
        ))}
      </AssessmentGrid>
    </ContentContainer>
  );
};
